import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const DISMISS_KEY = 'fbg_pwa_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Premium "Install App" prompt.
 * - Captures beforeinstallprompt on supported browsers (Chrome/Edge/Android).
 * - Hidden once installed (display-mode: standalone) or explicitly dismissed.
 */
export const InstallPWAPrompt = () => {
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed? Don't show.
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Recently dismissed? Respect user choice.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (_) { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const strings = {
    fr: {
      title: "Installer l'application FBG",
      desc: "Accédez à First Business Group en un clic, même hors ligne.",
      install: 'Installer',
      dismiss: 'Plus tard'
    },
    en: {
      title: 'Install the FBG app',
      desc: 'Access First Business Group in one tap, even offline.',
      install: 'Install',
      dismiss: 'Later'
    }
  };
  const t = strings[language === 'fr' ? 'fr' : 'en'];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="pwa-install-prompt"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 240, damping: 24 }}
          className="fixed bottom-6 right-6 z-[100] max-w-sm"
        >
          <div className="relative overflow-hidden rounded-2xl shadow-2xl border border-white/10"
               style={{ background: 'linear-gradient(135deg, #1B325F 0%, #2a4a8f 100%)' }}>
            {/* Accent stripe */}
            <div className="absolute top-0 left-0 right-0 h-1" style={{ background: '#DB242C' }} />

            <button
              data-testid="pwa-install-dismiss"
              onClick={handleDismiss}
              className="absolute top-2 right-2 p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label={t.dismiss}
            >
              <X className="h-4 w-4" />
            </button>

            <div className="p-5 pr-10 text-white">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <img src="/fbg-logo.png" alt="FBG" className="w-8 h-8 object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-serif text-lg font-bold leading-tight">{t.title}</h4>
                  <p className="text-sm text-white/80 mt-1 leading-snug">{t.desc}</p>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  data-testid="pwa-install-later"
                  onClick={handleDismiss}
                  className="px-3 py-2 text-sm text-white/80 hover:text-white transition-colors"
                >
                  {t.dismiss}
                </button>
                <button
                  data-testid="pwa-install-accept"
                  onClick={handleInstall}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all"
                  style={{ background: '#DB242C' }}
                >
                  <Download className="h-4 w-4" />
                  {t.install}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPWAPrompt;
